import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
import type { ReactNode } from "react";

import { Accent, Section, SectionHead, panel } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const orb =
  "grid size-[46px] place-items-center rounded-full outline-none transition-[background-color,transform] focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]";
const drawing = cn(panel, "relative w-4/5 p-3.5 text-xs");

const ScheduleDrawing = () => (
  <div className={cn(drawing, "relative grid grid-cols-[34px_1fr] gap-x-2")}>
    {["07:00", "09:00", "11:00", "13:00"].map((hour) => (
      <span key={hour} className="col-start-1 h-9 text-[10px] text-muted-foreground tabular-nums">
        {hour}
      </span>
    ))}
    <span className="absolute top-3.5 right-3.5 left-[56px] flex h-8 items-center rounded-[6px] bg-primary px-2 text-[11px] font-semibold text-primary-foreground">
      <span className="truncate">{m.platform_preview_ola()}</span>
    </span>
    <span className="absolute top-[86px] right-3.5 left-[56px] flex h-8 items-center rounded-[6px] bg-[var(--site-well)] px-2 text-[11px] font-semibold">
      <span className="truncate">{m.platform_preview_consult()}</span>
    </span>
    <span className="absolute top-[124px] right-3.5 left-[56px] h-px bg-foreground" />
  </div>
);

const PlanDrawing = () => (
  <div className={drawing}>
    <div className="flex gap-1">
      {[1, 2, 3, 4].map((week) => (
        <span
          key={week}
          className={cn(
            "rounded-full px-2.5 py-1 text-[11px] font-semibold",
            week === 2 ? "bg-foreground text-background" : "bg-[var(--site-well)] text-muted-foreground",
          )}
        >
          {m.platform_rail_week({ n: week })}
        </span>
      ))}
    </div>
    {[
      [m.platform_plan_squat(), "4 × 6"],
      [m.platform_plan_rdl(), "3 × 8"],
      [m.platform_plan_row(), "3 × 10"],
    ].map(([name, sets]) => (
      <div key={name} className="mt-2 flex items-center justify-between rounded-lg bg-[var(--site-well)] px-2.5 py-2">
        <span className="truncate font-medium">{name}</span>
        <b className="text-[11px] font-semibold tabular-nums">{sets}</b>
      </div>
    ))}
  </div>
);

/** Two sample meals, each with its calories and its protein / carbs / fat split in percent. */
const MEALS = [
  { id: "breakfast", name: () => m.platform_diet_breakfast(), kcal: 520, split: [30, 55, 15] },
  { id: "lunch", name: () => m.platform_diet_lunch(), kcal: 680, split: [38, 45, 17] },
];
const MACRO_TONES = ["bg-primary", "bg-[var(--site-ink)]", "bg-muted-foreground/40"];

const DietDrawing = () => (
  <div className={cn(drawing, "grid gap-2")}>
    {MEALS.map((meal) => (
      <div key={meal.id} className="rounded-lg bg-[var(--site-well)] p-2.5">
        <div className="flex justify-between font-semibold">
          {meal.name()}
          <span className="text-muted-foreground tabular-nums">{m.platform_kcal({ amount: meal.kcal })}</span>
        </div>
        <div className="mt-2 flex h-1.5 overflow-hidden rounded-full">
          {meal.split.map((share, index) => (
            <i key={MACRO_TONES[index]} className={MACRO_TONES[index]} style={{ width: `${share}%` }} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ProgressDrawing = () => (
  <div className={cn(drawing, "pt-10")}>
    <span className="absolute top-3 left-3.5 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
      {m.platform_progress_record()}
    </span>
    {/* The dot shares the line's box, so a percentage of one is the same point on the other. */}
    <div className="relative h-[100px]">
      <svg viewBox="0 0 200 100" preserveAspectRatio="none" className="absolute inset-0 size-full">
        <path
          d="M0,90 L25,82 L45,86 L70,64 L95,68 L120,40 L140,52 L165,30 L200,36"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="absolute size-3 -translate-1/2 rounded-full bg-primary shadow-[0_0_0_4px_color-mix(in_oklch,var(--primary)_25%,transparent)]"
        style={{ left: "82.5%", top: "30%" }}
      />
    </div>
  </div>
);

const PassDrawing = () => (
  <div className={drawing}>
    <div className="flex items-center justify-between">
      <b className="text-[13px] font-semibold">{m.platform_pass_name()}</b>
      <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">
        {m.platform_pass_unpaid()}
      </span>
    </div>
    <div className="mt-3 grid grid-cols-10 gap-1">
      {Array.from({ length: 10 }, (_, index) => (
        <i key={index} className={cn("h-6 rounded-md", index < 7 ? "bg-primary" : "bg-[var(--site-well)]")} />
      ))}
    </div>
    <small className="mt-2 block text-muted-foreground">{m.platform_pass_left({ used: 7, total: 10 })}</small>
  </div>
);

const PageDrawing = () => (
  <div className={cn(panel, "w-4/5 overflow-hidden text-xs")}>
    <div className="flex items-center gap-1.5 bg-[var(--site-well)] px-3 py-2">
      <i className="size-2 rounded-full bg-muted-foreground/40" />
      <i className="size-2 rounded-full bg-muted-foreground/40" />
      <span className="ml-1.5 truncate rounded-md bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
        complete-athlete.co/kasia
      </span>
    </div>
    <div className="grid grid-cols-3 gap-1.5 p-3">
      {["07:00", "09:30", "17:00", "17:30", "18:30", "19:30"].map((time, index) => (
        <span
          key={time}
          className={cn(
            "grid h-7 place-items-center rounded-lg text-[11px] font-semibold tabular-nums",
            index === 3 ? "bg-primary text-primary-foreground" : "bg-[var(--site-well)]",
          )}
        >
          {time}
        </span>
      ))}
    </div>
  </div>
);

const tools = (): { id: string; title: string; body: string; art: ReactNode }[] => [
  {
    id: "schedule",
    title: m.platform_feature_schedule_title(),
    body: m.platform_rail_schedule_body(),
    art: <ScheduleDrawing />,
  },
  { id: "plans", title: m.platform_feature_plans_title(), body: m.platform_rail_plans_body(), art: <PlanDrawing /> },
  { id: "diet", title: m.platform_feature_diet_title(), body: m.platform_rail_diet_body(), art: <DietDrawing /> },
  {
    id: "progress",
    title: m.platform_feature_progress_title(),
    body: m.platform_rail_progress_body(),
    art: <ProgressDrawing />,
  },
  { id: "packs", title: m.platform_feature_packs_title(), body: m.platform_rail_packs_body(), art: <PassDrawing /> },
  { id: "page", title: m.platform_feature_page_title(), body: m.platform_rail_page_body(), art: <PageDrawing /> },
];

/**
 * Everything the app does for a trainer, as one row of cards to slide through: six tools, one
 * app. Each card is a drawing of the tool and two lines about it.
 *
 * `lead` when it opens a page of its own, which makes its title that page's h1.
 */
export const ToolsRail = ({ lead = false }: { lead?: boolean }) => {
  const rail = useRef<HTMLUListElement>(null);
  const slide = (direction: 1 | -1) => {
    const card = rail.current?.firstElementChild;
    rail.current?.scrollBy({ left: direction * ((card?.clientWidth ?? 380) + 12), behavior: "smooth" });
  };

  return (
    <Section id="tools">
      <div className="flex items-end justify-between gap-6">
        <div className="flex-1">
          <SectionHead
            as={lead ? "h1" : "h2"}
            eyebrow={m.platform_rail_eyebrow()}
            title={
              <>
                {m.platform_rail_title_lead()} <Accent>{m.platform_rail_title_tail()}</Accent>
              </>
            }
          />
        </div>
        <div className="mb-9 flex shrink-0 gap-2">
          <button
            type="button"
            aria-label={m.platform_rail_previous()}
            className={cn(orb, "bg-card hover:bg-muted")}
            onClick={() => slide(-1)}
          >
            <ChevronLeft aria-hidden className="size-[18px]" />
          </button>
          <button
            type="button"
            aria-label={m.platform_rail_next()}
            className={cn(orb, "bg-primary text-primary-foreground hover:brightness-105")}
            onClick={() => slide(1)}
          >
            <ChevronRight aria-hidden className="size-[18px]" />
          </button>
        </div>
      </div>
      <ul
        ref={rail}
        className="grid snap-x snap-mandatory auto-cols-[minmax(280px,380px)] grid-flow-col gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tools().map((tool, index) => (
          <li key={tool.id} className="flex snap-start flex-col overflow-hidden rounded-[28px] bg-card">
            <div
              aria-hidden
              className="relative grid h-[260px] place-items-center overflow-hidden bg-[var(--site-tint)]"
            >
              {tool.art}
            </div>
            <div className="px-6 pt-5 pb-6">
              <span className="text-xs font-semibold tracking-[0.14em] text-[var(--site-ink)] tabular-nums">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-2.5 text-2xl font-medium tracking-[-0.03em]">{tool.title}</h3>
              <p className="mt-2 text-[15px] leading-normal text-muted-foreground">{tool.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
};
